import {Hero as PrimerHero, useWindowSize} from '@primer/react-brand'
import {useEffect, useRef} from 'react'
import {WebGLDither} from '@github-ui/brand-webgl'

import Styles from './Hero.module.css'

export interface HeroProps {
  label: string
  heading: string
  body: string
  trailingComponent?: React.FunctionComponent
  cta: {
    label: string
    href: string
  }
  visual: {
    desktop: {
      src: string
      alt: string
    }
    mobile: {
      src: string
      alt: string
    }
  }
}

export const Hero = ({label, heading, body, trailingComponent, cta, visual}: HeroProps) => {
  const wrapperRef = useRef<HTMLElement>(null)
  const {isLarge: isDesktopView} = useWindowSize()

  useEffect(() => {
    if (!wrapperRef.current) return

    const wrapper = wrapperRef.current
    const imageContainer: HTMLElement | null = wrapper.querySelector(`.${Styles['Hero__imageContainer']}`)
    const ditherSelector = `.${Styles['Hero__imageDither']}`
    if (!imageContainer) return

    const syncSize = () => {
      const imageDither: HTMLElement | null = wrapper.querySelector(ditherSelector)
      if (!imageDither) return
      const {width, height} = imageContainer.getBoundingClientRect()
      imageDither.style.width = `${width}px`
      imageDither.style.height = `${height}px`
    }

    const resizeObserver = new ResizeObserver(syncSize)
    resizeObserver.observe(imageContainer)

    // The dither element only enters the DOM once `WebGLDither`'s lazy chunk resolves.
    // Watch for it, sync once, then stop observing.
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && (node.matches(ditherSelector) || node.querySelector(ditherSelector))) {
            syncSize()
            mutationObserver.disconnect()
            return
          }
        }
      }
    })
    mutationObserver.observe(wrapper, {childList: true, subtree: true})

    syncSize()

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  return (
    <section ref={wrapperRef} className={Styles['Hero']}>
      <PrimerHero
        variant="gridline-expressive"
        enableAnimation
        imageContainerClassName={Styles['Hero__imageContainer']}
        trailingComponent={trailingComponent}
      >
        <PrimerHero.Label>{label}</PrimerHero.Label>
        <PrimerHero.Heading>{heading}</PrimerHero.Heading>

        <PrimerHero.Description>{body}</PrimerHero.Description>
        <PrimerHero.PrimaryAction href={cta.href}>{cta.label}</PrimerHero.PrimaryAction>

        <PrimerHero.Image
          as="picture"
          className={Styles['Hero__image']}
          src={visual.desktop.src}
          alt={isDesktopView ? visual.desktop.alt : visual.mobile.alt}
          sources={[
            {srcset: visual.mobile.src, media: '(max-width: 543px)'},
            {srcset: visual.desktop.src, media: '(min-width: 544px)'},
          ]}
          enableBorder={false}
          position={isDesktopView ? 'block-end' : 'inline-end-padded'}
        />
      </PrimerHero>

      <WebGLDither
        className={Styles['Hero__imageDither']}
        params={{color: 'default', type: 'wave', fadeIn: true, dotOpacity: 0.2}}
      />
    </section>
  )
}
